import React, { useMemo, useState } from 'react'

import {
	Box,
	Checkbox,
	Dialog,
	FormControlLabel,
	IconButton,
	MenuItem,
	TextField,
	ToggleButton,
	ToggleButtonGroup,
	Typography,
} from '@mui/material'

import ChevronLeftRoundedIcon from '@mui/icons-material/ChevronLeftRounded'

import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded'

import CloseRoundedIcon from '@mui/icons-material/CloseRounded'

import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'

import TableRowsIcon from '@mui/icons-material/TableRows'

import ViewHeadlineIcon from '@mui/icons-material/ViewHeadline'

import { type GridColDef } from '@mui/x-data-grid-pro'

import { useModal } from '../../../../../store/ModalProvider'

import CustomButton from '../../CustomButton/CustomButton'
import { AnimatedDataGridPro } from '../../CustomDataGrid'
import { dataGridDensityTransitionSx } from '../../CustomDataGrid/dataGridDensityTransitionSx'
import CustomColumnMenu, { columnMenuPopperSx } from '../../CustomDataGrid/CustomColumnMenu'
import { popupInputLabelSx } from '../popupInputStyles'
import { popupActionButtonSx, popupSecondaryButtonSx } from '../popupButtonStyles'

import type { SendScheduleModalData } from '../../../../../types/modal'

import servicesMock from '../../../../../data/mockedData/schedules/services.json'

import scheduledServicesMock from '../../../../../data/mockedData/schedules/scheduledServices.json'

type ScheduleRow = {
	id: string

	startDate: string

	startTime: string

	service: string

	startLocation: string

	endLocation: string

	endTime: string

	endDate: string

	client: string

	observations: string

	driver?: string

	vehicle?: string
}

const dialogShadow =
	'0px 9px 46px 8px rgba(9, 32, 86, 0.12), 0px 24px 38px 3px rgba(9, 32, 86, 0.14), 0px 11px 15px -7px rgba(9, 32, 86, 0.2)'

const tableColumns = [
	{ key: 'startDate', label: 'Data In\u00edcio', width: '90px' },

	{ key: 'startTime', label: 'Hora In\u00edcio', width: '80px' },

	{ key: 'service', label: 'Servi\u00e7o', width: '85px' },

	{ key: 'startLocation', label: 'Local In\u00edcio', width: '120px' },

	{ key: 'endLocation', label: 'Local Término', width: '130px' },

	{ key: 'endTime', label: 'Hora Término', width: '90px' },

	{ key: 'endDate', label: 'Data Término', width: '90px' },

	{ key: 'client', label: 'Cliente', width: '140px' },

	{ key: 'observations', label: 'Observa\u00e7\u00f5es', width: '140px' },

	{ key: 'driver', label: 'Motorista', width: '120px' },
]

const checkboxSx = {
	color: '#8695AF',

	'&.Mui-checked': {
		color: '#00A5FF',
	},

	'&.MuiCheckbox-indeterminate': {
		color: '#00A5FF',
	},
}

const outlinedFieldSx = {
	'& .MuiInputLabel-root': {
		...popupInputLabelSx,
	},
	'& .MuiOutlinedInput-root': {
		height: '40px',

		backgroundColor: '#FFFFFF',

		borderRadius: '6px',

		fontSize: '13px',

		fontWeight: 500,

		fontFamily: 'Poppins, sans-serif',

		color: '#092056',

		'& fieldset': {
			borderColor: 'rgba(134, 149, 175, 0.3)',
		},

		'& .MuiSelect-icon': {
			color: '#00A5FF',
		},
	},

	'& .MuiOutlinedInput-input': {
		padding: '10px 12px',
	},
}

const headerTextSx = {
	fontSize: '12px',

	fontWeight: 600,

	color: '#8695AF',

	letterSpacing: '0.17px',

	lineHeight: '24px',

	fontFamily: 'Poppins, sans-serif',
}

const cellTextSx = {
	fontSize: '12px',

	color: '#092056',

	lineHeight: 1.3,

	fontFamily: 'Poppins, sans-serif',
}

const tableSx = {
	border: 'none',
	'--DataGrid-rowBorderColor': 'transparent',
	'& .MuiDataGrid-columnHeaders': {
		backgroundColor: '#FFFFFF',
		borderBottom: '1px solid rgba(134, 149, 175, 0.15)',
		borderTop: 'none',
		boxShadow: 'none',
	},
	'& .MuiDataGrid-columnHeaderTitle': {
		...headerTextSx,
	},
	'& .MuiDataGrid-columnSeparator': {
		opacity: 0,
		color: 'rgba(134, 149, 175, 0.6)',
		transition: 'opacity 0.2s ease',
	},
	'& .MuiDataGrid-columnHeader:hover .MuiDataGrid-columnSeparator': {
		opacity: 1,
	},
	'& .MuiDataGrid-columnSeparator--resizable': {
		cursor: 'col-resize',
	},
	'& .MuiDataGrid-columnHeader, & .MuiDataGrid-cell': {
		borderRight: 'none',
	},
	'& .MuiDataGrid-cell': {
		...cellTextSx,
		borderBottom: 'none',
		whiteSpace: 'nowrap',
		overflow: 'hidden',
		textOverflow: 'ellipsis',
	},
	'& .MuiDataGrid-row': {
		borderBottom: 'none',
	},
	'& .MuiDataGrid-row.missing-assignment:not(.Mui-selected)': {
		backgroundColor: 'rgba(255, 216, 150, 0.35)',
	},
	'& .MuiDataGrid-row.Mui-selected': {
		backgroundColor: 'rgba(0, 165, 255, 0.08)',
	},
	'& .MuiDataGrid-row.Mui-selected:hover': {
		backgroundColor: 'rgba(0, 165, 255, 0.12)',
	},
	'& .MuiDataGrid-cell:focus, & .MuiDataGrid-columnHeader:focus': {
		outline: 'none',
	},
}

const gridColumns: GridColDef<ScheduleRow>[] = tableColumns.map(column => ({
	field: column.key,
	headerName: column.label,
	width: Number.parseInt(column.width, 10),
}))

const toggleGroupSx = {
	border: '1px solid rgba(0, 165, 255, 0.3)',

	borderRadius: '6px',

	height: '24px',

	'& .MuiToggleButton-root': {
		border: 'none',

		padding: '2px',

		width: '24px',

		height: '24px',

		color: '#00A5FF',

		'&.Mui-selected': {
			backgroundColor: 'rgba(0, 165, 255, 0.15)',

			color: '#00A5FF',
		},

		'&:hover': {
			backgroundColor: 'rgba(0, 165, 255, 0.08)',
		},
	},
}

const formatValue = (value?: string) => {
	if (!value || value.trim() === '') {
		return '-'
	}

	return value
}

const normalizeRows = (rows: Array<Partial<ScheduleRow>>) => {
	return rows.map((row, index) => ({
		id: row.id ? String(row.id) : `row-${index}`,

		startDate: formatValue(row.startDate),

		startTime: formatValue(row.startTime),

		service: formatValue(row.service ?? (row as { description?: string }).description),

		startLocation: formatValue(row.startLocation),

		endLocation: formatValue(row.endLocation),

		endTime: formatValue(row.endTime),

		endDate: formatValue(row.endDate),

		client: formatValue(row.client),

		observations: formatValue(row.observations),

		driver: row.driver,

		vehicle: row.vehicle,
	}))
}

type TableSectionProps = {
	title: string

	rows: ScheduleRow[]

	selectedRows: string[]

	onSelectionChange: (selectedRows: string[]) => void

	viewMode: 'table' | 'list'

	onViewModeChange: (mode: 'table' | 'list') => void

	headerExtras?: React.ReactNode

	highlightMissing?: boolean

	maxHeight?: string
}

const TableSection: React.FC<TableSectionProps> = ({
	title,

	rows,

	selectedRows,

	onSelectionChange,

	viewMode,

	onViewModeChange,

	headerExtras,

	highlightMissing,

	maxHeight = '200px',
}) => {
	const rowHeight = 32

	return (
		<Box sx={{ display: 'grid', gap: '8px' }}>
			<Box
				sx={{
					display: 'flex',

					alignItems: 'center',

					justifyContent: 'space-between',

					gap: '16px',
				}}
			>
				<Typography
					sx={{
						fontSize: '18px',

						fontWeight: 600,

						color: '#092056',

						fontFamily: 'Poppins, sans-serif',
					}}
				>
					{title}
				</Typography>

				<Box sx={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
					{headerExtras}

					<ToggleButtonGroup
						exclusive
						value={viewMode}
						onChange={(_, value) => value && onViewModeChange(value)}
						sx={toggleGroupSx}
					>
						<ToggleButton
							value='table'
							aria-label='Tabela'
						>
							<TableRowsIcon sx={{ fontSize: '16px' }} />
						</ToggleButton>

						<ToggleButton
							value='list'
							aria-label='Lista'
						>
							<ViewHeadlineIcon sx={{ fontSize: '16px' }} />
						</ToggleButton>
					</ToggleButtonGroup>
				</Box>
			</Box>

			<Box
				sx={{
					borderTop: '1px solid rgba(134, 149, 175, 0.2)',

					borderBottom: '1px solid rgba(134, 149, 175, 0.2)',

					overflowX: 'auto',
				}}
			>
				<Box sx={{ height: maxHeight }}>
					<AnimatedDataGridPro
						slots={{ columnMenu: CustomColumnMenu }}
						slotProps={{ basePopper: { sx: columnMenuPopperSx } }}
						rows={rows}
						columns={gridColumns}
						density='compact'
						rowHeight={rowHeight}
						columnHeaderHeight={40}
						checkboxSelection
						rowSelectionModel={selectedRows}
						onRowSelectionModelChange={newSelection => {
							onSelectionChange(newSelection as string[])
						}}
						disableRowSelectionOnClick
						hideFooter
						getRowClassName={params => {
							if (highlightMissing && (!params.row.driver?.trim() || !params.row.vehicle?.trim())) {
								return 'missing-assignment'
							}
							return ''
						}}
						sx={[tableSx, dataGridDensityTransitionSx]}
					/>
				</Box>
			</Box>
		</Box>
	)
}

const CustomModalSendSchedule: React.FC = () => {
	const { isOpen, closeModal, modalData, modalHandlers } = useModal()

	const sendScheduleData = modalData as SendScheduleModalData | undefined

	const [handlePrint, handleSend] = modalHandlers ?? []

	const [zone, setZone] = useState('Todas')

	const [sendEmail, setSendEmail] = useState(true)

	const [groupByZone, setGroupByZone] = useState(false)

	const [considerExternal, setConsiderExternal] = useState(false)

	const [onlyWithoutDriver, setOnlyWithoutDriver] = useState(false)

	const [onlyWithoutVehicle, setOnlyWithoutVehicle] = useState(false)

	const [unscheduledView, setUnscheduledView] = useState<'table' | 'list'>('table')

	const [scheduledView, setScheduledView] = useState<'table' | 'list'>('table')

	const [selectedUnscheduledRows, setSelectedUnscheduledRows] = useState<string[]>([])

	const [selectedScheduledRows, setSelectedScheduledRows] = useState<string[]>([])

	const unscheduledRows = useMemo(
		() => normalizeRows(servicesMock as unknown as ScheduleRow[]),

		[],
	)

	const scheduledRows = useMemo(() => {
		const source = sendScheduleData?.scheduledServices?.length
			? sendScheduleData.scheduledServices
			: scheduledServicesMock

		return normalizeRows(source as unknown as ScheduleRow[])
	}, [sendScheduleData])

	const filteredScheduledRows = useMemo(() => {
		return scheduledRows.filter(row => {
			if (onlyWithoutDriver && row.driver?.trim()) {
				return false
			}

			if (onlyWithoutVehicle && row.vehicle?.trim()) {
				return false
			}

			return true
		})
	}, [scheduledRows, onlyWithoutDriver, onlyWithoutVehicle])

	if (!sendScheduleData) {
		return null
	}

	const handlePrintSchedule = () => {
		if (typeof handlePrint === 'function') {
			handlePrint(filteredScheduledRows)

			return
		}

		console.log('Imprimir escala', filteredScheduledRows)
	}

	const handleSendSchedule = () => {
		if (typeof handleSend === 'function') {
			handleSend(filteredScheduledRows)

			return
		}

		console.log('Enviar escala', filteredScheduledRows)
	}

	return (
		<Dialog
			open={isOpen}
			onClose={closeModal}
			maxWidth={false}
			sx={{ pointerEvents: 'auto', zIndex: 2000 }}
			PaperProps={{
				sx: {
					width: 'min(1200px, 96vw)',

					height: '806px',

					borderRadius: '6px',

					backgroundColor: '#FFFFFF',

					boxShadow: dialogShadow,

					overflow: 'hidden',

					zIndex: 2001,
				},
			}}
		>
			<Box
				sx={{
					display: 'flex',

					flexDirection: 'column',

					height: '100%',

					fontFamily: 'Poppins, sans-serif',

					color: '#092056',
				}}
			>
				<Box
					sx={{
						padding: '16px 24px 0',
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'space-between',
						gap: '16px',
					}}
				>
					<Typography
						sx={{
							fontSize: '16px',
							fontWeight: 600,
							lineHeight: 1.75,
							letterSpacing: '0.15px',
							color: '#092056',
						}}
					>
						Enviar Escala do Dia
					</Typography>
					<IconButton
						aria-label='Fechar'
						onClick={closeModal}
						sx={{ padding: '4px' }}
					>
						<CloseRoundedIcon sx={{ fontSize: 20, color: '#00A5FF' }} />
					</IconButton>
				</Box>

				<Box sx={{ flex: 1, padding: '16px 24px 24px', overflow: 'hidden' }}>
					<Box sx={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
						<TextField
							select
							label='Zona'
							value={zone}
							onChange={event => setZone(event.target.value)}
							sx={{ ...outlinedFieldSx, minWidth: '237px' }}
							SelectProps={{ IconComponent: KeyboardArrowDownIcon }}
							InputLabelProps={{ shrink: true }}
						>
							<MenuItem value='Todas'>Todas</MenuItem>
						</TextField>

						<FormControlLabel
							control={
								<Checkbox
									size='small'
									checked={sendEmail}
									onChange={event => setSendEmail(event.target.checked)}
									sx={checkboxSx}
								/>
							}
							label='Enviar por email'
							sx={{
								margin: 0,

								'.MuiTypography-root': {
									fontFamily: 'Poppins, sans-serif',

									fontSize: '13px',

									fontWeight: 500,

									color: '#092056',
								},
							}}
						/>

						<FormControlLabel
							control={
								<Checkbox
									size='small'
									checked={groupByZone}
									onChange={event => setGroupByZone(event.target.checked)}
									sx={checkboxSx}
								/>
							}
							label='Agrupar motoristas e viaturas por zona'
							sx={{
								margin: 0,

								'.MuiTypography-root': {
									fontFamily: 'Poppins, sans-serif',

									fontSize: '13px',

									fontWeight: 500,

									color: '#092056',
								},
							}}
						/>

						<FormControlLabel
							control={
								<Checkbox
									size='small'
									checked={considerExternal}
									onChange={event => setConsiderExternal(event.target.checked)}
									sx={checkboxSx}
								/>
							}
							label='Considerar escalado quando o motorista é externo'
							sx={{
								margin: 0,

								'.MuiTypography-root': {
									fontFamily: 'Poppins, sans-serif',

									fontSize: '13px',

									fontWeight: 500,

									color: '#092056',
								},
							}}
						/>
					</Box>

					<Box
						sx={{
							display: 'grid',

							gap: '24px',

							marginTop: '20px',

							height: 'calc(100% - 60px)',
						}}
					>
						<TableSection
							title={`${unscheduledRows.length} Por Escalar`}
							rows={unscheduledRows}
							selectedRows={selectedUnscheduledRows}
							onSelectionChange={setSelectedUnscheduledRows}
							viewMode={unscheduledView}
							onViewModeChange={setUnscheduledView}
							maxHeight='140px'
						/>

						<Box sx={{ display: 'grid', gap: '12px' }}>
							<TableSection
								title={`${filteredScheduledRows.length} Escalados`}
								rows={filteredScheduledRows}
								selectedRows={selectedScheduledRows}
								onSelectionChange={setSelectedScheduledRows}
								viewMode={scheduledView}
								onViewModeChange={setScheduledView}
								headerExtras={
									<Box sx={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
										<FormControlLabel
											control={
												<Checkbox
													size='small'
													checked={onlyWithoutDriver}
													onChange={event => setOnlyWithoutDriver(event.target.checked)}
													sx={checkboxSx}
												/>
											}
											label='Só Sem Motorista'
											sx={{
												margin: 0,

												'.MuiTypography-root': {
													fontFamily: 'Poppins, sans-serif',

													fontSize: '12px',

													fontWeight: 600,

													color: '#092056',
												},
											}}
										/>

										<FormControlLabel
											control={
												<Checkbox
													size='small'
													checked={onlyWithoutVehicle}
													onChange={event => setOnlyWithoutVehicle(event.target.checked)}
													sx={checkboxSx}
												/>
											}
											label='Só Sem Viatura'
											sx={{
												margin: 0,

												'.MuiTypography-root': {
													fontFamily: 'Poppins, sans-serif',

													fontSize: '12px',

													fontWeight: 600,

													color: '#092056',
												},
											}}
										/>
									</Box>
								}
								highlightMissing
								maxHeight='220px'
							/>

							<Box
								sx={{
									display: 'flex',

									justifyContent: 'flex-end',

									alignItems: 'center',

									gap: '12px',

									color: '#8695AF',
								}}
							>
								<Typography
									sx={{
										fontSize: '12px',
										fontWeight: 500,
										fontFamily: 'Poppins, sans-serif',
									}}
								>
									Linhas por página
								</Typography>
								<Box
									sx={{
										display: 'flex',
										alignItems: 'center',
										gap: '4px',
									}}
								>
									<Typography
										sx={{
											fontSize: '12px',

											fontWeight: 600,

											color: '#092056',

											fontFamily: 'Poppins, sans-serif',
										}}
									>
										10
									</Typography>

									<KeyboardArrowDownIcon sx={{ fontSize: '16px', color: '#00A5FF' }} />
								</Box>

								<Typography
									sx={{
										fontSize: '12px',

										fontWeight: 500,

										fontFamily: 'Poppins, sans-serif',
									}}
								>
									1-5 de 13
								</Typography>

								<IconButton
									size='small'
									sx={{ color: '#00A5FF' }}
								>
									<ChevronLeftRoundedIcon fontSize='small' />
								</IconButton>

								<IconButton
									size='small'
									sx={{ color: '#00A5FF' }}
								>
									<ChevronRightRoundedIcon fontSize='small' />
								</IconButton>
							</Box>
						</Box>
					</Box>
				</Box>

				<Box
					sx={{
						padding: '12px 24px 16px',

						display: 'flex',

						justifyContent: 'flex-end',

						gap: '12px',
					}}
				>
					<CustomButton
						variant='text'
						size='medium'
						onClick={closeModal}
						sx={popupSecondaryButtonSx}
					>
						Fechar
					</CustomButton>

					<CustomButton
						variant='text'
						size='medium'
						onClick={handlePrintSchedule}
						sx={popupSecondaryButtonSx}
					>
						Imprimir
					</CustomButton>

					<CustomButton
						variant='contained'
						size='medium'
						onClick={handleSendSchedule}
						sx={popupActionButtonSx}
					>
						Enviar
					</CustomButton>
				</Box>
			</Box>
		</Dialog>
	)
}

export default CustomModalSendSchedule
